import { useQuery } from '@apollo/client/react';
import { createFileRoute, useBlocker } from '@tanstack/react-router';
import { useState } from 'react';
import { useBreadcrumbLabel } from '../../components/BreadcrumbLabelsContext.js';
import { usePagedCursor } from '../../hooks/use-paged-cursor.js';
import { assertUuidRouteParams } from '../../lib/assert-uuid-route-params.js';
import { ActivityTimeline } from '../../modules/activity/activity-timeline.js';
import { usePermissions } from '../../modules/iam/permissions-context.js';
import { purchaseOrderDetailLookupAccess } from '../../modules/purchasing/purchase-order-detail-lookup-access.js';
import {
  GET_PURCHASE_ORDER_HEADER,
  GET_PURCHASE_ORDER_LINE_ITEMS,
  GET_PURCHASE_ORDER_RECEIPTS,
  GET_PURCHASE_ORDER_SHIPMENTS,
  PO_DETAIL_LINE_ITEMS_PAGE_SIZE,
  type GetPurchaseOrderDetailQueryVariables,
  type GetPurchaseOrderHeaderQueryResult,
  type GetPurchaseOrderHeaderQueryVariables,
  type GetPurchaseOrderLineItemsQueryResult,
  type GetPurchaseOrderLineItemsQueryVariables,
  type GetPurchaseOrderReceiptsQueryResult,
  type GetPurchaseOrderShipmentsQueryResult,
} from '../../modules/purchasing/purchase-order-detail-operations.js';
import { PurchaseOrderDetailView } from '../../modules/purchasing/purchase-order-detail-view.js';
import { usePurchaseOrderActivity } from '../../modules/purchasing/use-purchase-order-activity.js';

export const Route = createFileRoute('/purchase-orders/$purchaseOrderId')({
  beforeLoad: ({ params }) => assertUuidRouteParams(params),
  component: PurchaseOrderDetailPage,
});

// eslint-disable-next-line react-refresh/only-export-components
function PurchaseOrderDetailPage() {
  const { purchaseOrderId } = Route.useParams();
  return <PurchaseOrderDetailPageContent key={purchaseOrderId} purchaseOrderId={purchaseOrderId} />;
}

type PurchaseOrderDetailPageContentProps = {
  purchaseOrderId: string;
};

// eslint-disable-next-line react-refresh/only-export-components
function PurchaseOrderDetailPageContent({ purchaseOrderId }: PurchaseOrderDetailPageContentProps) {
  const { permissions, loading: permissionsLoading } = usePermissions();
  const { canViewCarriers, ...headerAccess } = purchaseOrderDetailLookupAccess(permissions);
  const headerVariables: GetPurchaseOrderHeaderQueryVariables = {
    id: purchaseOrderId,
    ...headerAccess,
  };
  const linesPager = usePagedCursor();
  const [isDirty, setIsDirty] = useState(false);

  // Guards in-app exits, which the detail view's `beforeunload` never sees.
  useBlocker({
    shouldBlockFn: () => (isDirty ? !window.confirm('Discard unsaved changes?') : false),
    // Defaults to true, which would prompt on every reload of a clean page.
    enableBeforeUnload: () => isDirty,
  });

  const {
    data: headerData,
    loading: headerLoading,
    refetch: refetchHeader,
  } = useQuery<GetPurchaseOrderHeaderQueryResult, GetPurchaseOrderHeaderQueryVariables>(
    GET_PURCHASE_ORDER_HEADER,
    { variables: headerVariables, skip: permissionsLoading },
  );

  const {
    data: linesData,
    loading: linesLoading,
    error: linesError,
    refetch: refetchLines,
  } = useQuery<GetPurchaseOrderLineItemsQueryResult, GetPurchaseOrderLineItemsQueryVariables>(
    GET_PURCHASE_ORDER_LINE_ITEMS,
    {
      variables: {
        id: purchaseOrderId,
        after: linesPager.cursor,
        first: PO_DETAIL_LINE_ITEMS_PAGE_SIZE,
      },
      // Header and lines are separate operations; avoid normalized cache merge conflicts.
      fetchPolicy: 'no-cache',
    },
  );

  const { data: receiptsData, refetch: refetchReceipts } = useQuery<
    GetPurchaseOrderReceiptsQueryResult,
    GetPurchaseOrderDetailQueryVariables & { canViewUsers: boolean }
  >(GET_PURCHASE_ORDER_RECEIPTS, {
    variables: { id: purchaseOrderId, canViewUsers: headerAccess.canViewUsers },
    skip: permissionsLoading,
    fetchPolicy: 'no-cache',
  });

  const { data: shipmentsData, refetch: refetchShipments } = useQuery<
    GetPurchaseOrderShipmentsQueryResult,
    GetPurchaseOrderDetailQueryVariables & { canViewCarriers: boolean }
  >(GET_PURCHASE_ORDER_SHIPMENTS, {
    variables: { id: purchaseOrderId, canViewCarriers },
    skip: permissionsLoading,
    fetchPolicy: 'no-cache',
  });

  const { refetch: refetchActivity, ...activity } = usePurchaseOrderActivity(purchaseOrderId);

  const purchaseOrder = headerData?.purchaseOrder;
  useBreadcrumbLabel(purchaseOrderId, purchaseOrder?.num);
  const lineItemsConnection = linesData?.purchaseOrder?.purchaseOrderItem;
  const lineItems = lineItemsConnection?.edges.map((edge) => edge.node) ?? [];
  const lineItemsPageInfo = lineItemsConnection?.pageInfo;
  const receipts = receiptsData?.purchaseOrder?.receipt ?? [];
  const shipments = shipmentsData?.purchaseOrder?.ship ?? [];

  return (
    <div>
      {/* A refetch must not unmount the view, or its in-page state (reorder lines, line drafts) is lost. */}
      {(permissionsLoading || headerLoading) && !headerData ? (
        <div className="text-sm text-muted-foreground">Loading...</div>
      ) : (
        <PurchaseOrderDetailView
          purchaseOrder={purchaseOrder}
          lineItems={lineItems}
          linesLoading={linesLoading}
          linesError={linesError?.message}
          lineItemsPageInfo={lineItemsPageInfo}
          receipts={receipts}
          shipments={shipments}
          activityTab={<ActivityTimeline {...activity} />}
          onLinesNextPage={() => linesPager.goToNextPage(lineItemsPageInfo?.endCursor)}
          onLinesPreviousPage={linesPager.goToPreviousPage}
          onDirtyChange={setIsDirty}
          onChanged={() => {
            void refetchHeader();
            void refetchLines();
            void refetchReceipts();
            void refetchShipments();
            void refetchActivity();
          }}
          onDetailRefetch={async () => {
            const result = await refetchHeader();
            return result.data?.purchaseOrder;
          }}
        />
      )}
    </div>
  );
}
